import React, { useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { useTradingRulesConfig } from '../../contexts/TradingRulesConfigContext';
import PositionsTable from '../PositionsTable';
import AccountValueCard from '../AccountValueCard/AccountValueCard';
import DashboardMobileTabHeader from './DashboardMobileTabHeader';
import WebullStyleTabs from '../shared/WebullStyleTabs';
import SchwabPnLInfoBanner from '../shared/SchwabPnLInfoBanner';
import PerformanceDashboard from '../../pages/PerformanceDashboard';
import TradingRulesDashboard from '../../pages/TradingRulesDashboard';
import TradingRulesForm from '../TradingRules/TradingRulesForm';
import JournalPage from './JournalPage';
import OrdersTable from '../OrdersTable';
import TradeAlertsManager from '../TradeAlerts/TradeAlertsManager';
import useAccountValueCardData from '../../hooks/useAccountValueCardData';
import { isTrackTier } from '../../utils/authAccess';
import { isConnectTradeSchwabAccount } from '../../utils/brokerConnection';
import { MOBILE_BOTTOM_NAV_CLEARANCE } from '../../constants/mobileBottomNav';
import { MOBILE_UI } from '../../constants/mobileUi';
import BacktestWorkspace from '../../backtest/components/BacktestWorkspace';
import { getPortfolioTabs } from '../../backtest/components/PortfolioBacktestTabs';

const PORTFOLIO_TABS = [
  { id: 'assets', label: 'Assets' },
  { id: 'performance', label: 'Performance' },
  { id: 'health', label: 'Health' },
  { id: 'trading-rules', label: 'Trading Rules' },
  { id: 'journals', label: 'Journals' },
  { id: 'orders', label: 'Orders' },
  { id: 'alerts', label: 'Alerts' },
];

function PortfolioAssetsTab({ onViewAccountHealth }) {
  const { selectedAccount } = useContext(TradingContext);
  const {
    cardMetrics,
    netWorth,
    netWorthLoading,
    netWorthBreakdown,
    showNetWorth,
    openPositionsCount,
    ordersFilledTodayCount,
    violationsTodayCount,
    statsLoading,
    violationsLoading,
    loading,
    rulesMetricsLoading,
  } = useAccountValueCardData();

  return (
    <div data-testid="portfolio-assets-tab">
      <div style={{ padding: MOBILE_UI.pagePadding }}>
        {isConnectTradeSchwabAccount(selectedAccount) && (
          <SchwabPnLInfoBanner style={{ marginBottom: 12 }} />
        )}
        <AccountValueCard
          equity={cardMetrics.equity}
          dayPL={cardMetrics.dayPL}
          openPL={cardMetrics.openPL}
          realizedPL={cardMetrics.realizedPL}
          buyingPower={cardMetrics.buyingPower}
          cash={cardMetrics.cash}
          marketValue={cardMetrics.marketValue}
          netWorth={netWorth}
          netWorthLoading={netWorthLoading}
          netWorthBreakdown={netWorthBreakdown}
          showNetWorth={showNetWorth}
          openPositionsCount={openPositionsCount}
          ordersFilledTodayCount={ordersFilledTodayCount}
          violationsTodayCount={violationsTodayCount}
          statsLoading={statsLoading}
          violationsLoading={violationsLoading}
          loading={loading}
          isMobile
          variant="default"
          showPrivacyToggle={false}
          showBottomStats
          onViewAccountHealth={
            !rulesMetricsLoading ? onViewAccountHealth : undefined
          }
        />
      </div>
      <div style={{ padding: `0 ${MOBILE_UI.pagePadding}px` }}>
        <PositionsTable renderAsChild showMobileSectionTitle />
      </div>
    </div>
  );
}

function PortfolioTradingRulesTab() {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const trackTier = isTrackTier(user);
  const {
    settings: tradingRulesSettings,
    loading: tradingRulesLoading,
    saveSettings: saveTradingRulesSettings,
  } = useTradingRulesConfig();

  if (tradingRulesLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>
        Loading settings...
      </div>
    );
  }

  return (
    <TradingRulesForm
      accountId={selectedAccount?.AccountID}
      settings={tradingRulesSettings}
      saveSettings={saveTradingRulesSettings}
      standalone
      layoutVariant="ruleConfigurationPage"
      trackTier={trackTier}
    />
  );
}

/**
 * Mobile Portfolio — horizontal section tabs for Assets, Performance, Health,
 * Trading Rules, Journals, Orders, Alerts, and Backtest (admins).
 */
const PortfolioPage = () => {
  const { colors } = useTheme();
  const { orders } = useContext(TradingContext);
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(() => {
    const requested = location.state?.portfolioTab;
    if (requested === 'backtest' && user?.superuser) return 'backtest';
    if (requested === 'health' || requested === 'performance') return requested;
    return 'assets';
  });

  const openOrdersCount = (orders || []).filter((o) =>
    o.Status === 'Open' || o.Status === 'Working'
  ).length;

  const tabsWithBadges = getPortfolioTabs(user, PORTFOLIO_TABS).map((tab) =>
    tab.id === 'orders' ? { ...tab, badge: openOrdersCount || undefined } : tab
  );

  return (
    <div
      data-testid="portfolio-page"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.bgPrimary,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: MOBILE_BOTTOM_NAV_CLEARANCE,
        overflow: 'hidden',
      }}
    >
      <DashboardMobileTabHeader
        title="Portfolio"
        menuCurrentPage="portfolio"
        showAccountDropdown
      />

      <div style={{ padding: `8px ${MOBILE_UI.pagePadding}px 0`, flexShrink: 0 }}>
        <WebullStyleTabs
          tabs={tabsWithBadges}
          activeTab={activeTab}
          onChange={setActiveTab}
          variant="filled"
        />
      </div>

      <div style={{ flex: 1, overflowY: activeTab === 'backtest' ? 'hidden' : 'auto', minHeight: 0 }}>
        {activeTab === 'assets' && (
          <PortfolioAssetsTab onViewAccountHealth={() => setActiveTab('health')} />
        )}

        {activeTab === 'performance' && (
          <div style={{ padding: MOBILE_UI.pagePadding }}>
            <PerformanceDashboard hideHeader hideDashboardHeader embeddedMobile />
          </div>
        )}

        {activeTab === 'health' && (
          <div data-testid="portfolio-health-tab" style={{ padding: MOBILE_UI.pagePadding }}>
            <TradingRulesDashboard hideHeader hideDashboardHeader embeddedMobile />
          </div>
        )}

        {activeTab === 'trading-rules' && (
          <div style={{ padding: MOBILE_UI.pagePadding }}>
            <PortfolioTradingRulesTab />
          </div>
        )}

        {activeTab === 'journals' && (
          <JournalPage hideHeader menuCurrentPage="portfolio" />
        )}

        {activeTab === 'orders' && (
          <div style={{ padding: `0 ${MOBILE_UI.pagePadding}px` }}>
            <OrdersTable />
          </div>
        )}

        {activeTab === 'alerts' && (
          <div style={{ padding: MOBILE_UI.pagePadding }}>
            <TradeAlertsManager renderAsChild />
          </div>
        )}

        {activeTab === 'backtest' && (
          <div style={{ height: '100%', minHeight: 0, padding: `0 ${MOBILE_UI.pagePadding}px 8px`, display: 'flex', flexDirection: 'column' }}>
            <BacktestWorkspace embedded />
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioPage;
